import styled from 'styled-components';
import { useState } from 'react';
import { ChevronDown } from 'react-feather';
import { useAuth } from '../lib/AuthProvider.js';

/** MerchantSwitcher — ports `.merchant-pill` (active merchant + switch context). */
const Root = styled.div`
  position: relative;
  margin-bottom: 24px;
`;
const Pill = styled.button`
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  width: 100%;
  text-align: left;
  color: inherit;
`;
const Menu = styled.div`
  position: absolute;
  z-index: 20;
  left: 0;
  right: 0;
  top: calc(100% + 6px);
  background: #151515;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 8px;
  padding: 6px;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.25);
`;
const MenuItem = styled.button<{ $active: boolean }>`
  width: 100%;
  border: 0;
  border-radius: 6px;
  background: ${({ $active }) => ($active ? 'rgba(255,255,255,0.1)' : 'transparent')};
  color: #fff;
  cursor: ${({ $active }) => ($active ? 'default' : 'pointer')};
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 10px;
  text-align: left;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;
const Logo = styled.span`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: #fff;
  color: ${({ theme }) => theme.color.kBlack};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 12px;
`;
const Info = styled.span`
  flex: 1;
`;
const Name = styled.span`
  display: block;
  font-size: 13px;
  font-weight: 600;
`;
const RoleLabel = styled.span`
  display: block;
  font-size: 10px;
  opacity: 0.6;
  text-transform: capitalize;
`;
const Chevron = styled.span<{ $open: boolean }>`
  display: flex;
  align-items: center;
  opacity: 0.6;
  transition: transform 0.15s ease;
  transform: rotate(${({ $open }) => ($open ? 180 : 0)}deg);
`;

export function MerchantSwitcher() {
  const { me, organisations, selectedOrganisation, switchOrg } = useAuth();
  const [open, setOpen] = useState(false);
  const [busyOrg, setBusyOrg] = useState<string | null>(null);
  const selectedId = selectedOrganisation?.id ?? null;
  const role = selectedOrganisation?.role ?? me?.activeRole ?? 'member';
  const name = selectedOrganisation?.name ?? 'No merchant';
  const initial = (selectedOrganisation?.name ?? me?.user.email ?? 'K').charAt(0).toUpperCase();

  async function onSwitch(nextOrgId: string) {
    if (nextOrgId === selectedId || busyOrg) return;
    setBusyOrg(nextOrgId);
    try {
      await switchOrg(nextOrgId);
      setOpen(false);
    } finally {
      setBusyOrg(null);
    }
  }

  return (
    <Root>
      <Pill
        type="button"
        title="Switch merchant"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        disabled={organisations.length <= 1}
      >
        <Logo>{initial}</Logo>
        <Info>
          <Name>{name}</Name>
          <RoleLabel>{role}</RoleLabel>
        </Info>
        <Chevron aria-hidden $open={open}>
          <ChevronDown size={16} />
        </Chevron>
      </Pill>
      {open && organisations.length > 1 && (
        <Menu role="menu">
          {organisations.map((o) => (
            <MenuItem
              key={o.id}
              type="button"
              role="menuitem"
              $active={o.id === selectedId}
              disabled={o.id === selectedId || busyOrg != null}
              onClick={() => void onSwitch(o.id)}
            >
              <Name>{o.name}</Name>
              <RoleLabel>{busyOrg === o.id ? 'Switching' : o.role}</RoleLabel>
            </MenuItem>
          ))}
        </Menu>
      )}
    </Root>
  );
}
